import { omit } from 'lodash'

// Types
import type { TrackList, AudioState, Loop, TrackFeatures } from 'types'

const initialState: AudioState = {
  beatOffset: 0,
  currentLoop: null,
  currentPlaylist: '',
  features: {},
  from: '',
  isEnded: false,
  isHotkeySeeking: false,
  isLoading: false,
  isLooping: false,
  isPaused: false,
  isPlaying: false,
  playlists: {},
  songItemId: '0',
  trackId: 0,
  url: '',
}

type AudioPlayTrackAction = {
  type: 'AUDIO__PLAY_TRACK'
  data: {
    trackId: number
    songItemId: string
    contextKey: string
  }
}

type AudioSetPausedAction = {
  type: 'AUDIO__SET_PAUSED'
  data: boolean
}

type AudioLoadStartAction = {
  type: 'AUDIO__LOAD_START'
  data: {
    trackId: number
    songItemId: string
  }
}

type AudioSetPlaylistAction = {
  type: 'AUDIO__SET_PLAYLIST'
  data?: {
    playlist: TrackList
    contextKey: string
    setAsActivePlaylist: boolean
  }
}

type AudioClearPlaylistAction = {
  type: 'AUDIO__CLEAR_PLAYLIST'
  data: string
}

type AudioSelectPlaylistAction = {
  type: 'AUDIO__SELECT_PLAYLIST'
  data: string
}

type AudioSongTrackEndAction = {
  type: 'AUDIO__SONG_TRACK_END'
}

type AudioSetIsLoading = {
  type: 'AUDIO__SET_IS_LOADING'
  data: boolean
}
type AudioSongClearAction = {
  type: 'AUDIO__SONG_CLEAR'
}

type FetchTrackFeatureDataAction = {
  type: 'FETCH_TRACK_FEATURE_DATA_DONE'
  data: {
    trackId: number
    response: {
      features: {
        rhythm: TrackFeatures
      }
    }
  }
}
type AudioSetIsLoopingAction = {
  type: 'AUDIO__SET_IS_LOOPING'
  data: boolean
}

type AudioStartPlayingLoop = {
  type: 'AUDIO__START_PLAYING_LOOP'
}

type AudioSetBeatOffsetAction = {
  type: 'AUDIO__SET_BEAT_OFFSET'
  data: number
}

type AudioActionSetSongItemId = {
  type: 'AUDIO__SET_SONG_ITEM_ID'
  data: string
}

type AudioSetCurrentLoop = {
  type: 'AUDIO__SET_CURRENT_LOOP'
  data: Loop
}

type AudioSetHotkeySeeking = {
  type: 'AUDIO__SET_HOTKEY_SEEKING'
  data: boolean
}

type AudioSeturlAction = {
  type: 'AUDIO__SET_URL'
  data: string
}

type AudioResetAction = {
  type: 'AUDIO__RESET'
}

type AudioStateActions =
  | AudioPlayTrackAction
  | AudioSetPausedAction
  | AudioLoadStartAction
  | AudioSetPlaylistAction
  | AudioClearPlaylistAction
  | AudioSelectPlaylistAction
  | AudioSongTrackEndAction
  | AudioSetIsLoading
  | AudioSongClearAction
  | FetchTrackFeatureDataAction
  | AudioSetIsLoopingAction
  | AudioStartPlayingLoop
  | AudioSetBeatOffsetAction
  | AudioActionSetSongItemId
  | AudioSetCurrentLoop
  | AudioSetHotkeySeeking
  | AudioSeturlAction
  | AudioResetAction

export const reducer = (state = initialState, action: AudioStateActions): AudioState => {
  if (!action.type || typeof action.type !== 'string') {
    return state
  }

  switch (action.type) {
    case 'AUDIO__PLAY_TRACK':
      return {
        ...state,
        isPaused: false,
        isPlaying: true,
        trackId: action.data.trackId,
        songItemId: action.data.songItemId,
        isLooping: false,
        isLoading: false,
        from: action.data.contextKey,
      }

    case 'AUDIO__SET_PAUSED':
      return {
        ...state,
        isPaused: action.data,
      }

    case 'AUDIO__LOAD_START': {
      const { trackId, songItemId } = action.data
      return {
        ...state,
        isLoading: true,
        trackId,
        songItemId,
      }
    }

    case 'AUDIO__SET_PLAYLIST': {
      if (!action.data) {
        return state
      }
      const {
        data: { playlist, contextKey, setAsActivePlaylist },
      } = action
      let newState = state
      if (setAsActivePlaylist) {
        newState = {
          ...newState,
          currentPlaylist: contextKey,
        }
      }

      // NOTE (Kitti): Filter out songs without songId (unpublished)
      return {
        ...newState,
        playlists: {
          ...newState.playlists,
          [contextKey]: playlist.filter(({ trackId, songId }) => !!trackId && !!songId),
        },
      }
    }

    case 'AUDIO__CLEAR_PLAYLIST':
      return {
        ...state,
        playlists: omit(state.playlists, action.data),
      }

    case 'AUDIO__SELECT_PLAYLIST':
      return {
        ...state,
        currentPlaylist: action.data,
      }

    case 'AUDIO__SONG_TRACK_END':
      return {
        ...state,
        isEnded: true,
        isPaused: true,
        isPlaying: false,
      }

    case 'AUDIO__SET_IS_LOADING': {
      return {
        ...state,
        isLoading: action.data,
      }
    }

    case 'AUDIO__SONG_CLEAR':
      return {
        ...state,
        isPlaying: false,
        isPaused: false,
        isLooping: false,
        isLoading: false,
        isEnded: false,
        trackId: 0,
      }

    case 'FETCH_TRACK_FEATURE_DATA_DONE': {
      const {
        data: { trackId, response },
      } = action
      if (response?.features?.rhythm) {
        return {
          ...state,
          features: {
            ...state.features,
            [String(trackId)]: response.features.rhythm,
          },
        }
      }
      return state
    }

    case 'AUDIO__SET_IS_LOOPING': {
      return {
        ...state,
        isLooping: action.data,
        currentLoop: null,
      }
    }

    case 'AUDIO__START_PLAYING_LOOP': {
      return {
        ...state,
        isLooping: true,
        isPlaying: true,
        isPaused: false,
        isLoading: false,
      }
    }

    case 'AUDIO__SET_BEAT_OFFSET': {
      return {
        ...state,
        beatOffset: action.data,
      }
    }

    case 'AUDIO__SET_SONG_ITEM_ID': {
      return {
        ...state,
        songItemId: action.data,
      }
    }

    case 'AUDIO__SET_CURRENT_LOOP': {
      return {
        ...state,
        currentLoop: action.data,
      }
    }

    case 'AUDIO__SET_HOTKEY_SEEKING': {
      return {
        ...state,
        isHotkeySeeking: action.data,
      }
    }

    case 'AUDIO__SET_URL':
      return {
        ...state,
        url: action.data,
      }

    case 'AUDIO__RESET': {
      return initialState
    }

    default:
      return state
  }
}

export default reducer
